/**
 * useDirectusProxy
 *
 * Composable for all Directus data fetching. Routes every request through the
 * server-side proxy at /api/directus-proxy which injects the admin token,
 * CDN-Tag headers, and retry logic — keeping secrets off the client.
 *
 * The cuid and ne query params are added here (matching legacy useDirectus
 * behaviour) so Directus receives them on every request.
 */

interface DirectusProxyOptions {
  operation: 'readItems' | 'readItem' | 'readSingleton' | 'createItem' | 'readFiles' | 'readFolders';
  collection: string;
  params?: Record<string, unknown>;
  item?: Record<string, unknown>;
  id?: string | number;
  /** When set, the proxy authenticates with this token instead of the admin token (preview mode) */
  previewToken?: string;
  /** Content version key for versioned collections (e.g., pages) */
  previewVersion?: string;
}

export const useDirectusProxy = async <T = unknown>(options: DirectusProxyOptions): Promise<T> => {
  const { operation, collection, params = {}, item, id, previewToken, previewVersion } = options;

  const runtimeConfig = useRuntimeConfig();
  const cuidValue = runtimeConfig.public.directus?.rest?.zorba as string;
  const nuxtEnv = (runtimeConfig.public.nuxtEnv as string) || 'development';
  const nodeEnv = (runtimeConfig.public.nodeEnv as string) || 'development';

  let envValue: string;
  if (nodeEnv === 'development') envValue = 'dev';
  else if (nuxtEnv === 'staging') envValue = 'stage';
  else envValue = 'prod';

  // Append cuid + ne to read query params only. Create payloads must remain item fields.
  const enhancedParams: Record<string, unknown> =
    operation === 'createItem'
      ? params
      : {
          ...params,
          cuid: cuidValue,
          ne: envValue,
        };

  return await $fetch<T>('/api/directus-proxy', {
    method: 'POST',
    body: {
      operation,
      collection,
      params: enhancedParams,
      ...(item ? { item } : {}),
      id,
      ...(previewToken ? { previewToken } : {}),
      ...(previewVersion ? { previewVersion } : {}),
    },
  });
};
